import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAssessment } from '../contexts/AssessmentContext';
import { supabase } from '../lib/supabase';
import { questions } from '../data/questions';
import { harmonicQuestions } from '../data/harmonicQuestions';
import { characteristics } from '../data/characteristics';
import { harmonicLevels } from '../data/harmonicLevels';
import type { Answer, Score } from '../types';
import type { HarmonicAnswer, HarmonicScore } from '../types/harmonic';

const TOTAL_CHARACTERISTIC_QUESTIONS = 144;
const TOTAL_HARMONIC_QUESTIONS = 36;
const TOTAL_QUESTIONS = TOTAL_CHARACTERISTIC_QUESTIONS + TOTAL_HARMONIC_QUESTIONS;

function seededShuffle<T>(array: T[], seed: number): T[] {
  const result = [...array];
  let currentSeed = seed;

  const random = () => {
    currentSeed = (currentSeed * 1103515245 + 12345) & 0x7fffffff;
    return currentSeed / 0x7fffffff;
  };

  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }

  return result;
}

function computeCharacteristicScores(answers: Answer[]) {
  return characteristics.map(characteristic => {
    const matched = answers.filter(answer => {
      const question = questions.find(q => q.id === answer.questionId);
      return question?.characteristicId === characteristic.id;
    });

    const raw = matched.reduce((sum, answer) => {
      const question = questions.find(q => q.id === answer.questionId);
      const adjustedValue = question?.reversed ? (8 - answer.value) : answer.value;
      return sum + adjustedValue;
    }, 0);

    const maxPossible = 12 * 7;
    const normalized = (raw / maxPossible) * 100;

    return { characteristicId: characteristic.id, raw, normalized };
  });
}

function computeHarmonicScores(answers: HarmonicAnswer[]) {
  return harmonicLevels.map(level => {
    const levelAnswers = answers.filter(answer => {
      const question = harmonicQuestions.find(q => q.id === answer.questionId);
      return question?.levelId === level.id;
    });

    const raw = levelAnswers.reduce((sum, answer) => {
      const question = harmonicQuestions.find(q => q.id === answer.questionId);
      const adjustedValue = question?.reversed ? (8 - answer.value) : answer.value;
      return sum + adjustedValue;
    }, 0);

    const maxPossible = 3 * 7;
    const normalized = (raw / maxPossible) * 100;

    return { levelId: level.id, raw, normalized };
  });
}

async function saveResultsToDb(scores: Score[], harmonicScores: HarmonicScore[]) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('No authenticated user');

  const userId = session.user.id;

  const { data: respondent, error: respondentError } = await supabase
    .from('respondents')
    .select('id, coach_id, total_assessments')
    .eq('user_id', userId)
    .maybeSingle();

  if (respondentError) throw respondentError;
  if (!respondent) throw new Error('No respondent record found');

  const totalAssessments = respondent.total_assessments || 0;
  const isRetake = totalAssessments > 0;

  let originalResultId: string | null = null;
  if (isRetake) {
    const { data: originalResult } = await supabase
      .from('results')
      .select('id')
      .eq('user_id', userId)
      .order('completed_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (originalResult) originalResultId = originalResult.id;
  }

  const version = isRetake ? totalAssessments + 1 : 1;

  const { data: insertedResult, error: insertError } = await supabase
    .from('results')
    .insert({
      user_id: userId,
      coach_id: respondent.coach_id || null,
      scores: scores as any,
      harmonic_scores: harmonicScores as any,
      version,
      original_result_id: originalResultId,
      completed_at: new Date().toISOString(),
    })
    .select('id')
    .single();

  if (insertError) throw insertError;

  const nextRetakeDate = new Date();
  nextRetakeDate.setDate(nextRetakeDate.getDate() + 7);

  await supabase
    .from('respondents')
    .update({
      last_assessment_date: new Date().toISOString(),
      total_assessments: totalAssessments + 1,
      next_retake_date: nextRetakeDate.toISOString(),
      updated_at: new Date().toISOString(),
    })
    .eq('id', respondent.id);

  const { data: respondentFlags } = await supabase
    .from('respondents')
    .select('ai_analysis_enabled')
    .eq('id', respondent.id)
    .maybeSingle();

  if (respondentFlags?.ai_analysis_enabled && insertedResult?.id) {
    triggerAutoAiGeneration(insertedResult.id, scores, harmonicScores).catch(err =>
      console.error('Auto AI generation failed:', err)
    );
  }

  if (respondent.coach_id) {
    notifyCoachOfCompletion(respondent.coach_id, userId).catch(err =>
      console.error('Coach notification failed:', err)
    );
  }
}

async function notifyCoachOfCompletion(coachId: string, respondentUserId: string) {
  const { data: coachRecord } = await supabase
    .from('coaches')
    .select('user_id, account_plan, free_profile_completed_assessments, free_profile_first_completion_date')
    .eq('id', coachId)
    .maybeSingle();

  if (!coachRecord) return;

  const [coachUserResult, respondentUserResult] = await Promise.all([
    supabase.from('users').select('email, full_name').eq('id', coachRecord.user_id).maybeSingle(),
    supabase.from('users').select('full_name').eq('id', respondentUserId).maybeSingle(),
  ]);

  const coachEmail = coachUserResult.data?.email;
  const coachName = coachUserResult.data?.full_name || 'Coach';
  const respondentName = respondentUserResult.data?.full_name || 'A respondent';

  if (!coachEmail) return;

  if (coachRecord.account_plan === 'FREE_PROFILE_COACH') {
    const updates: Record<string, unknown> = {
      free_profile_completed_assessments: (coachRecord.free_profile_completed_assessments || 0) + 1,
      updated_at: new Date().toISOString(),
    };
    if (!coachRecord.free_profile_first_completion_date) {
      updates.free_profile_first_completion_date = new Date().toISOString();
    }
    await supabase.from('coaches').update(updates).eq('id', coachId);
  }

  const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
  const resultsUrl = `${siteUrl}/coach/respondents`;

  const { data: { session } } = await supabase.auth.getSession();

  const emailResponse = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      ...(session?.access_token ? { 'Authorization': `Bearer ${session.access_token}` } : {}),
    },
    body: JSON.stringify({
      type: 'assessment-completed',
      recipientEmail: coachEmail,
      recipientName: coachName,
      respondentName,
      coachName,
      resultsUrl,
    }),
  });

  if (!emailResponse.ok) {
    const errBody = await emailResponse.text().catch(() => '');
    console.error('Coach notification email failed:', emailResponse.status, errBody);
  }
}

async function triggerAutoAiGeneration(resultId: string, scores: Score[], harmonicScores: HarmonicScore[]) {
  const { buildAIPrompt } = await import('../components/ai/buildAIPrompt');
  const prompt = buildAIPrompt(
    scores,
    harmonicScores,
    'provide a comprehensive analysis of this person\'s potential, strengths, and areas for development. Include specific recommendations for personal growth.'
  );

  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-analysis`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ prompt }),
  });

  if (!response.ok) return;

  const data = await response.json();
  await supabase
    .from('results')
    .update({
      ai_analysis: data.analysis,
      ai_analysis_generated_at: new Date().toISOString(),
    })
    .eq('id', resultId);
}

async function saveDraft(
  userId: string,
  currentIndex: number,
  characteristicAnswers: Answer[],
  harmonicAnswers: HarmonicAnswer[]
) {
  await supabase
    .from('assessment_drafts')
    .upsert(
      {
        user_id: userId,
        assessment_type: 'standard',
        current_index: currentIndex,
        characteristic_answers: characteristicAnswers as any,
        harmonic_answers: harmonicAnswers as any,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'user_id,assessment_type' }
    );
}

async function deleteDraft(userId: string) {
  await supabase
    .from('assessment_drafts')
    .delete()
    .eq('user_id', userId)
    .eq('assessment_type', 'standard');
}

export async function clearAssessmentDraft(userId: string) {
  await deleteDraft(userId);
}

export function useQuestionnaire() {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [characteristicAnswers, setCharacteristicAnswers] = useState<Answer[]>([]);
  const [harmonicAnswers, setHarmonicAnswers] = useState<HarmonicAnswer[]>([]);
  const [pendingCompletion, setPendingCompletion] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [draftProgress, setDraftProgress] = useState<number | null>(null);
  const { setScores, setHarmonicScores } = useAssessment();

  const isAdvancingRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const userIdRef = useRef<string | null>(null);

  const allQuestionsRef = useRef<{ question: typeof questions[0] | typeof harmonicQuestions[0]; isHarmonic: boolean }[]>();
  if (!allQuestionsRef.current) {
    const shuffledHarmonic = seededShuffle(harmonicQuestions, 43);
    const combined: { question: typeof questions[0] | typeof harmonicQuestions[0]; isHarmonic: boolean }[] = [];

    for (let i = 0; i < 12; i++) {
      const charStart = i * 12;
      const charEnd = charStart + 12;
      const charBatch = questions.slice(charStart, charEnd).map(q => ({ question: q, isHarmonic: false }));

      const harmStart = i * 3;
      const harmEnd = harmStart + 3;
      const harmBatch = shuffledHarmonic.slice(harmStart, harmEnd).map(q => ({ question: q, isHarmonic: true }));

      combined.push(...charBatch, ...harmBatch);
    }

    allQuestionsRef.current = combined;
  }
  const allQuestions = allQuestionsRef.current;

  // Restore draft on mount
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user || cancelled) return;
      userIdRef.current = session.user.id;
      supabase
        .from('assessment_drafts')
        .select('current_index, characteristic_answers, harmonic_answers')
        .eq('user_id', session.user.id)
        .eq('assessment_type', 'standard')
        .maybeSingle()
        .then(({ data }) => {
          if (cancelled) return;
          if (data && data.current_index > 0) {
            setDraftProgress(data.current_index);
          } else {
            setDraftLoaded(true);
          }
        });
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resumeDraft = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    const { data } = await supabase
      .from('assessment_drafts')
      .select('current_index, characteristic_answers, harmonic_answers')
      .eq('user_id', session.user.id)
      .eq('assessment_type', 'standard')
      .maybeSingle();
    if (!data) return;
    setCharacteristicAnswers((data.characteristic_answers as Answer[]) || []);
    setHarmonicAnswers((data.harmonic_answers as HarmonicAnswer[]) || []);
    setCurrentIndex(data.current_index);
    setDraftProgress(null);
    setDraftLoaded(true);
  }, []);

  const discardDraft = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    await deleteDraft(session.user.id);
    setDraftProgress(null);
    setDraftLoaded(true);
  }, []);

  // Debounced save after each answer
  useEffect(() => {
    if (!draftLoaded || pendingCompletion) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      userIdRef.current = session.user.id;
      await saveDraft(session.user.id, currentIndex, characteristicAnswers, harmonicAnswers);
    }, 1000);
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [currentIndex, characteristicAnswers, harmonicAnswers, draftLoaded, pendingCompletion]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [currentIndex]);

  useEffect(() => {
    if (!pendingCompletion) return;

    const finalCharScores = computeCharacteristicScores(characteristicAnswers);
    const finalHarmScores = computeHarmonicScores(harmonicAnswers);

    setScores(finalCharScores);
    setHarmonicScores(finalHarmScores);

    const saveAndNavigate = async () => {
      try {
        await saveResultsToDb(finalCharScores, finalHarmScores);
        if (userIdRef.current) {
          await deleteDraft(userIdRef.current);
        }
      } catch (err) {
        console.error('Error saving results during completion:', err);
      }
      // Backup scores to sessionStorage so ResultsView can recover them
      // even if React context hasn't hydrated yet on navigation
      sessionStorage.setItem('assessment_scores_backup', JSON.stringify({
        scores: finalCharScores,
        harmonicScores: finalHarmScores,
      }));
      navigate('/results');
    };

    saveAndNavigate();
  }, [pendingCompletion, characteristicAnswers, harmonicAnswers, setScores, setHarmonicScores, navigate]);

  const currentQuestionData = allQuestions[currentIndex];
  const currentQuestion = currentQuestionData?.question;
  const isHarmonic = currentQuestionData?.isHarmonic ?? false;

  const getCurrentAnswer = useCallback(() => {
    if (!currentQuestion) return undefined;

    if (isHarmonic) {
      const answer = harmonicAnswers.find(a => a.questionId === currentQuestion.id);
      return answer?.value;
    } else {
      const answer = characteristicAnswers.find(a => a.questionId === currentQuestion.id);
      return answer?.value;
    }
  }, [currentQuestion, isHarmonic, characteristicAnswers, harmonicAnswers]);

  const handleAnswer = useCallback((questionId: string, value: number) => {
    if (isHarmonic) {
      setHarmonicAnswers(prev => {
        const existing = prev.findIndex(a => a.questionId === questionId);
        if (existing !== -1) {
          const updated = [...prev];
          updated[existing] = { questionId, value };
          return updated;
        }
        return [...prev, { questionId, value }];
      });
    } else {
      setCharacteristicAnswers(prev => {
        const existing = prev.findIndex(a => a.questionId === questionId);
        if (existing !== -1) {
          const updated = [...prev];
          updated[existing] = { questionId, value };
          return updated;
        }
        return [...prev, { questionId, value }];
      });
    }
  }, [isHarmonic]);

  const goToNext = useCallback(() => {
    if (isAdvancingRef.current || pendingCompletion) return;
    isAdvancingRef.current = true;

    if (currentIndex < TOTAL_QUESTIONS - 1) {
      setCurrentIndex(prev => prev + 1);
      isAdvancingRef.current = false;
    } else {
      setPendingCompletion(true);
    }
  }, [currentIndex, pendingCompletion]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  }, [currentIndex]);

  return {
    currentQuestionNumber: currentIndex + 1,
    totalQuestions: TOTAL_QUESTIONS,
    question: currentQuestion,
    isHarmonic,
    handleAnswer,
    goToNext,
    goToPrevious,
    currentAnswer: getCurrentAnswer(),
    canGoBack: currentIndex > 0,
    draftProgress,
    resumeDraft,
    discardDraft,
  };
}
